import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Select, Typography, showToast } from '@attentive-platform/stem-ui';

import useScopeRecords from './useScopeRecords';
import { catalogToTradeFullView, fetchScopeCatalog, fetchScopeRecord } from './api';
import { CatalogTabStyles as S } from './styles';
import ExampleCards, { normalizeExampleUrls } from '../CustomerScopeForm/ExampleCards';
import { SearchOmniboxStyles as SO } from '../CustomerScopeForm/styles';

const MODES = [
  { key: 'compact', label: 'Compact' },
  { key: 'full', label: 'Full' }
];

const LENS_NONE = '';

const itemCountLabel = n => `${n} ${n === 1 ? 'item' : 'items'}`;
const tradeCountLabel = (items, sections) =>
  `${itemCountLabel(items)} · ${sections} ${sections === 1 ? 'section' : 'sections'}`;

const CatalogTab = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeTradeId, setActiveTradeId] = useState(null);
  const [mode, setMode] = useState('compact');
  const [expandedItems, setExpandedItems] = useState({});
  const [lens, setLens] = useState(LENS_NONE);
  const [lensDetail, setLensDetail] = useState(null);
  const [lensLoading, setLensLoading] = useState(false);
  const [lensError, setLensError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCursor, setSearchCursor] = useState(0);
  const searchWrapRef = useRef(null);

  const { rows: linkedRecords } = useScopeRecords({
    linked: true,
    query: '',
    stateFilter: 'All states',
    userFilter: 'All internal users',
    pilotFilter: 'All pilots',
    dateFilter: 'All time'
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchScopeCatalog().then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || 'Failed to load catalog';
        showToast(msg, 'error');
        setPayload(null);
      } else {
        setPayload(data);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!lens) {
      setLensDetail(null);
      setLensError('');
      setLensLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLensLoading(true);
    setLensError('');
    fetchScopeRecord(lens, { signal: controller.signal }).then(({ data, error: err }) => {
      if (controller.signal.aborted) return;
      setLensLoading(false);
      if (err) {
        const detail = typeof err === 'string' ? err : err.detail || 'Could not load that company scope.';
        setLensError(detail);
        setLensDetail(null);
        showToast(detail, 'error');
        return;
      }
      setLensDetail(data);
    });
    return () => controller.abort();
  }, [lens]);

  const trades = useMemo(() => catalogToTradeFullView(payload, { query }), [payload, query]);
  const searching = query.trim().length >= 2;

  useEffect(() => {
    if (!trades.length) return;
    if (!activeTradeId || !trades.some(t => t.id === activeTradeId)) {
      setActiveTradeId(trades[0].id);
    }
  }, [trades, activeTradeId]);

  const activeTrade = trades.find(t => t.id === activeTradeId) || trades[0] || null;

  const lensOptions = useMemo(
    () =>
      [{ label: 'No comparison', value: LENS_NONE }].concat(
        linkedRecords.map(r => ({ label: r.company, value: String(r.id) }))
      ),
    [linkedRecords]
  );
  const lensRecord = lens ? linkedRecords.find(r => String(r.id) === lens) : null;

  const lensAssignedCount = useMemo(() => {
    if (!lensDetail?.trades_in_scope) return 0;
    return lensDetail.trades_in_scope.reduce(
      (a, t) => a + (t.sections || []).reduce((b, s) => b + (s.assigned_scopes || []).length, 0),
      0
    );
  }, [lensDetail]);

  const { lensSelectedElementIds, lensAssignedScopeIds } = useMemo(() => {
    const elIds = new Set();
    const scIds = new Set();
    (lensDetail?.trades_in_scope || []).forEach(t =>
      (t.sections || []).forEach(s =>
        (s.assigned_scopes || []).forEach(sc => {
          if (sc.id) scIds.add(sc.id);
          (sc.elements || []).forEach(el => {
            if (el.selected && el.id) elIds.add(el.id);
          });
        })
      )
    );
    return { lensSelectedElementIds: elIds, lensAssignedScopeIds: scIds };
  }, [lensDetail]);

  const lensOn = !!lensDetail;

  const searchIndex = useMemo(() => {
    const rows = [];
    (payload?.trades || []).forEach(t => {
      (t.sections || []).forEach(s => {
        (s.scopes || []).forEach(sc => {
          rows.push({
            kind: 'Item',
            label: sc.scope_name,
            path: `${t.trade_name}${s.section_name ? ` · ${s.section_name}` : ''}`,
            tradeId: t.trade_id,
            scopeId: sc.scope_id
          });
          (sc.elements || []).forEach(el => {
            rows.push({
              kind: 'Element',
              label: el.name,
              path: `${t.trade_name} · ${sc.scope_name}`,
              tradeId: t.trade_id,
              scopeId: sc.scope_id,
              elementId: el.id
            });
          });
        });
      });
    });
    return rows;
  }, [payload]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter(r => `${r.label} ${r.path}`.toLowerCase().includes(q))
      .slice(0, 20);
  }, [searchIndex, query]);

  useEffect(() => {
    setSearchCursor(0);
  }, [query]);

  useEffect(() => {
    const handler = e => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pickSearchResult = row => {
    if (!row) return;
    setActiveTradeId(row.tradeId);
    setExpandedItems(prev => ({ ...prev, [row.scopeId]: true }));
    setSearchOpen(false);
  };

  const onSearchKeyDown = e => {
    if (!searchOpen || !searchResults.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSearchCursor(c => Math.min(searchResults.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSearchCursor(c => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pickSearchResult(searchResults[searchCursor]);
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
    }
  };

  const totalHits = trades.reduce((a, t) => a + t.hitCount, 0);

  const toggleItem = itemId => {
    if (mode === 'full') return;
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  const isItemExpanded = itemId => mode === 'full' || !!expandedItems[itemId];

  return (
    <S.Wrap>
      <S.Header>
        <S.HeaderText>
          <Typography variant='subtitle1-medium'>Catalog</Typography>
        </S.HeaderText>
        <S.HeaderControls>
          <SO.Wrap ref={searchWrapRef} onKeyDown={onSearchKeyDown}>
            <Input
              size='large'
              placeholder='Search any item or element'
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              width='20rem'
            />
            {searchOpen && query.trim() && searchResults.length > 0 && (
              <SO.Panel>
                <SO.PanelLabel>
                  {searchResults.length} match{searchResults.length === 1 ? '' : 'es'}
                </SO.PanelLabel>
                {searchResults.map((r, i) => {
                  const active = i === searchCursor;
                  const inScope =
                    lensOn &&
                    (r.kind === 'Element'
                      ? lensSelectedElementIds.has(r.elementId)
                      : lensAssignedScopeIds.has(r.scopeId));
                  const Row = active ? SO.RowActive : SO.RowIdle;
                  return (
                    <Row
                      key={`${r.kind}-${r.elementId || r.scopeId}`}
                      onMouseEnter={() => setSearchCursor(i)}
                      onClick={() => pickSearchResult(r)}>
                      <SO.RowTop>
                        <SO.KindBadge>{r.kind}</SO.KindBadge>
                        <SO.RowLabel>{r.label}</SO.RowLabel>
                        {inScope && <SO.CheckedPill>in their scope</SO.CheckedPill>}
                      </SO.RowTop>
                      <SO.RowPath>{r.path}</SO.RowPath>
                    </Row>
                  );
                })}
                <SO.Footer>
                  <SO.HintText>↑ ↓ to move · Enter to open · Esc to close</SO.HintText>
                  <SO.HintText>{searchIndex.length} in catalog</SO.HintText>
                </SO.Footer>
              </SO.Panel>
            )}
            {searchOpen && query.trim() && searchResults.length === 0 && (
              <SO.NoResultsPanel>
                <SO.NoResultsHeading>Nothing matches &ldquo;{query}&rdquo;</SO.NoResultsHeading>
                <SO.NoResultsHint>Try a shorter word or a different phrasing.</SO.NoResultsHint>
              </SO.NoResultsPanel>
            )}
          </SO.Wrap>
          <S.LensWrap>
            <Select
              size='small'
              variant='std-filled'
              items={lensOptions}
              value={lens}
              onChange={setLens}
              placeholder='Compare against a company'
            />
          </S.LensWrap>
          <S.ModeToggle>
            {MODES.map(m => {
              const Chip = mode === m.key ? S.ModeChipOn : S.ModeChipOff;
              return (
                <Chip
                  key={m.key}
                  onClick={() => {
                    setMode(m.key);
                    setExpandedItems({});
                  }}>
                  {m.label}
                </Chip>
              );
            })}
          </S.ModeToggle>
        </S.HeaderControls>
      </S.Header>

      {lensRecord && (
        <S.LensBanner>
          <S.LensSummary>
            {lensRecord.company}
            {lensLoading
              ? ' — loading their scope…'
              : lensError
                ? ' — could not load their scope'
                : lensDetail
                  ? ` — ${lensAssignedCount} item${lensAssignedCount === 1 ? '' : 's'} in their scope`
                  : ''}
          </S.LensSummary>
          <S.LensHint>Nothing here changes their record.</S.LensHint>
          <S.LensClear onClick={() => setLens(LENS_NONE)}>Clear comparison</S.LensClear>
        </S.LensBanner>
      )}

      {loading ? (
        <S.LoadingBox>Loading catalog…</S.LoadingBox>
      ) : !trades.length ? (
        <S.EmptyBox>{searching ? 'No matches for that search.' : 'Catalog is empty.'}</S.EmptyBox>
      ) : (
        <S.Split>
          <S.Rail>
            <S.RailHead>
              <S.RailHeadLabel>{trades.length} trades</S.RailHeadLabel>
              {searching && (
                <S.RailHeadHits>
                  {totalHits} {totalHits === 1 ? 'match' : 'matches'}
                </S.RailHeadHits>
              )}
            </S.RailHead>
            {trades.map(t => {
              const isActive = t.id === activeTrade?.id;
              const pickedInTrade = lensOn
                ? t.sections.reduce(
                    (a, s) => a + s.items.filter(it => lensAssignedScopeIds.has(it.id)).length,
                    0
                  )
                : 0;
              const railCount = searching
                ? t.hitCount
                  ? `${t.hitCount} ${t.hitCount === 1 ? 'hit' : 'hits'}`
                  : '—'
                : lensOn
                  ? `${pickedInTrade}/${t.totalItems}`
                  : t.totalItems;
              return (
                <S.RailTradeWrap key={t.id}>
                  <S.RailTradeRow active={isActive} onClick={() => setActiveTradeId(t.id)}>
                    <S.RailTradeName active={isActive}>{t.name}</S.RailTradeName>
                    <S.RailTradeCount>{railCount}</S.RailTradeCount>
                  </S.RailTradeRow>
                  {isActive && t.sections.length > 0 && (
                    <S.RailSectionList>
                      {t.sections.map(s => {
                        const pickedInSection = lensOn
                          ? s.items.filter(it => lensAssignedScopeIds.has(it.id)).length
                          : 0;
                        return (
                          <S.RailSectionRow key={s.id}>
                            <S.RailSectionName>{s.name}</S.RailSectionName>
                            <S.RailSectionCount>
                              {lensOn ? `${pickedInSection}/${s.items.length}` : s.items.length}
                            </S.RailSectionCount>
                          </S.RailSectionRow>
                        );
                      })}
                    </S.RailSectionList>
                  )}
                </S.RailTradeWrap>
              );
            })}
          </S.Rail>

          <S.Panel>
            {activeTrade && (
              <>
                <S.PanelHead>
                  <S.PanelTitleRow>
                    <S.PanelTitle>{activeTrade.name}</S.PanelTitle>
                    <S.PanelCountLabel>
                      {tradeCountLabel(activeTrade.totalItems, activeTrade.totalSections)}
                    </S.PanelCountLabel>
                  </S.PanelTitleRow>
                  {activeTrade.description && <S.PanelDescription>{activeTrade.description}</S.PanelDescription>}
                </S.PanelHead>

                {activeTrade.sections.map(sec => (
                  <S.SectionBlock key={sec.id}>
                    <S.SectionHead>
                      <S.SectionEyebrow>{sec.name}</S.SectionEyebrow>
                      <S.SectionCount>{itemCountLabel(sec.items.length)}</S.SectionCount>
                    </S.SectionHead>
                    {sec.items.map(item => {
                      const expanded = isItemExpanded(item.id);
                      const scopePicked = lensOn && lensAssignedScopeIds.has(item.id);
                      return (
                        <S.ItemRow key={item.id} dimmed={lensOn && !scopePicked}>
                          <S.ItemTop>
                            <S.ItemLabelWrap onClick={() => toggleItem(item.id)}>
                              <S.ItemChevron>{expanded ? '▾' : '▸'}</S.ItemChevron>
                              <S.ItemLabel>{item.label}</S.ItemLabel>
                              {scopePicked && <S.ElementTag picked>in their scope</S.ElementTag>}
                              {item.advanced && (
                                <S.AdvancedPill>advanced — may affect turnaround &amp; price</S.AdvancedPill>
                              )}
                              {item.isAdditional && <S.KindPill>additional</S.KindPill>}
                              {item.isDefault && <S.KindPill>default selected</S.KindPill>}
                            </S.ItemLabelWrap>
                            <S.ItemSummary onClick={() => toggleItem(item.id)}>{item.summary}</S.ItemSummary>
                          </S.ItemTop>
                          {expanded && (
                            <S.ItemDetail>
                              {item.description && <S.ModifierBody>{item.description}</S.ModifierBody>}
                              {item.elements.length > 0 && (
                                <S.DetailBlock>
                                  <S.DetailEyebrow>
                                    Elements · {item.elements.length}
                                    {lensOn &&
                                      ` · ${item.elements.filter(el => lensSelectedElementIds.has(el.id)).length} picked`}
                                  </S.DetailEyebrow>
                                  <S.ElementRow>
                                    {item.elements.map(el => {
                                      const picked = lensOn && lensSelectedElementIds.has(el.id);
                                      return (
                                        <S.ElementTag
                                          key={el.id}
                                          picked={picked}
                                          dimmed={lensOn && !picked}>
                                          {el.label}
                                        </S.ElementTag>
                                      );
                                    })}
                                  </S.ElementRow>
                                </S.DetailBlock>
                              )}
                              {item.modifiers.map(m => (
                                <S.DetailBlock key={m.id}>
                                  <S.ModifierRow>
                                    <S.DetailEyebrow>{m.title}</S.DetailEyebrow>
                                    <S.KindPill>{m.kindLabel}</S.KindPill>
                                    {m.advanced && (
                                      <S.AdvancedPill>advanced — may affect turnaround &amp; price</S.AdvancedPill>
                                    )}
                                  </S.ModifierRow>
                                  {m.hasBody && <S.ModifierBody>{m.body}</S.ModifierBody>}
                                  {m.isInput ? (
                                    <S.InputDefault>text answer</S.InputDefault>
                                  ) : m.isExample ? null : (
                                    m.options.length > 0 && (
                                      <S.OptionRow>
                                        {m.options.map((o, i) => (
                                          <S.OptionChip key={`${m.id}-${i}`}>{o.label}</S.OptionChip>
                                        ))}
                                      </S.OptionRow>
                                    )
                                  )}
                                  {normalizeExampleUrls(m.urls).length > 0 && (
                                    <ExampleCards modifiers={[m]} />
                                  )}
                                </S.DetailBlock>
                              ))}
                            </S.ItemDetail>
                          )}
                        </S.ItemRow>
                      );
                    })}
                  </S.SectionBlock>
                ))}
              </>
            )}
          </S.Panel>
        </S.Split>
      )}
    </S.Wrap>
  );
};

export default CatalogTab;
